export type StoredData = Record<string, any>;

export type StorageTarget = 'local' | 'session';

export type SetItemOptions = {
  // Where to write. Omit to keep using whichever storage already holds the session.
  target?: StorageTarget;
  // Whether to drop the entry from the other storage. On by default so a login
  // choice wins; impersonation opts out to preserve the admin's own session.
  clearOther?: boolean;
};

class WebStorage {
  // eslint-disable-next-line no-useless-constructor
  constructor(private eventId: string) {}

  getLocalStorageKey(): string {
    return `platform-${this.eventId}`;
  }

  getRgpdLocalStorageKey(): string {
    return `rgpd-${this.eventId}`;
  }

  // A value that isn't valid JSON is treated as absent: `value[0] === '{'` alone lets
  // truncated content through, and JSON.parse throws in callers that don't catch.
  // eslint-disable-next-line class-methods-use-this
  private readStoredData(storage: Storage, key: string): StoredData | undefined {
    const value = storage.getItem(key);
    if (!value || value[0] !== '{') return undefined;
    try {
      return JSON.parse(value) as StoredData;
    } catch (e) {
      return undefined;
    }
  }

  // sessionStorage when the user did not check "stay connected", or while impersonating:
  // the session survives a refresh but not the tab being closed.
  private getActiveStorage(): Storage {
    return this.readStoredData(sessionStorage, this.getLocalStorageKey())
      ? sessionStorage
      : localStorage;
  }

  removeItem(): void {
    sessionStorage.removeItem(this.getLocalStorageKey());
    localStorage.removeItem(this.getLocalStorageKey());
  }

  getItem(): StoredData | undefined {
    // sessionStorage wins: while impersonating, both storages hold an entry and the
    // tab-scoped one is the active session. An unreadable entry must not shadow a valid
    // one in localStorage though, that would log out a user who did check "stay connected".
    return (
      this.readStoredData(sessionStorage, this.getLocalStorageKey()) ??
      this.readStoredData(localStorage, this.getLocalStorageKey())
    );
  }

  getRgpdRules(): StoredData | undefined {
    return this.readStoredData(localStorage, this.getRgpdLocalStorageKey());
  }

  // The target is only known at login/impersonation time. When omitted (user update,
  // restore), keep writing to whichever storage already holds the session.
  setItem(value: StoredData, { target, clearOther = true }: SetItemOptions = {}): void {
    if (!target) {
      this.getActiveStorage().setItem(this.getLocalStorageKey(), JSON.stringify(value));
      return;
    }
    const [storage, other] =
      target === 'local' ? [localStorage, sessionStorage] : [sessionStorage, localStorage];
    // A leftover entry in the other storage would otherwise take over on the next read
    if (clearOther) other.removeItem(this.getLocalStorageKey());
    storage.setItem(this.getLocalStorageKey(), JSON.stringify(value));
  }
}

export default WebStorage;
